import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { AgentTask } from '../types';
import { TASK_STATUS_COLOR } from './StatusBadge';
import { formatMs } from './DurationBadge';
import { summarizeTaskPrompt } from '../taskPromptSummary';

const { Text, Title } = Typography;

interface Props {
  task: AgentTask;
  onBack: () => void;
  onStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  onRefreshPlan?: () => void;
}

function useElapsed(task: AgentTask): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!['RUNNING', 'PLANNING', 'WAITING_FOR_APPROVAL'].includes(task.status)) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [task.status]);

  if (task.completed_at && task.started_at) {
    return formatMs(new Date(task.completed_at).getTime() - new Date(task.started_at).getTime());
  }
  if (task.started_at) {
    return formatMs(now - new Date(task.started_at).getTime());
  }
  return '';
}

const TERMINAL = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

const TaskStatusHeader: React.FC<Props> = ({
  task, onBack, onStart, onPause, onResume, onCancel, onRefreshPlan,
}) => {
  const elapsed = useElapsed(task);
  const color = TASK_STATUS_COLOR[task.status] || 'default';
  const taskTitle = summarizeTaskPrompt(task.goal, 90);

  return (
    <div
      style={{
        background: '#171717',
        borderBottom: '1px solid #2a2a2a',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      {/* Left: back + identity */}
      <Space size={10}>
        <Button
          className="praxys-console-header-action praxys-console-header-icon-button"
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
        />
        <Title
          level={5}
          ellipsis={{ tooltip: task.goal }}
          style={{ color: '#e6edf3', margin: 0, maxWidth: 480, fontSize: 14 }}
        >
          {taskTitle}
        </Title>
      </Space>

      {/* Center: status + elapsed */}
      <Space size={8}>
          {task.status === 'RUNNING' && (
			<span
			  style={{
			    display: 'inline-flex',
			    alignItems: 'center',
			    gap: 5,
			    background: '#2a2a2a',
			    border: '1px solid #3f3f3f',
			    borderRadius: 20,
			    padding: '3px 12px',
			    color: '#10b981',
			    fontSize: 12,
			    fontWeight: 500,
			  }}
            />
          )}
		  
		  {task.status === 'COMPLETED' && (
		    <span
		      style={{
		        display: 'inline-flex',
		        alignItems: 'center',
		        gap: 5,
		        background: '#2a2a2a',
		        border: '1px solid #3f3f3f',
		        borderRadius: 20,
		        padding: '3px 12px',
		        color: '#10b981',
		        fontSize: 12,
		        fontWeight: 500,
		      }}
		    >
			  <CheckCircleOutlined style={{ fontSize: 12 }} />
			  Completed
			</span>
		  )}
        {elapsed && (
          <Text style={{ color: '#8b949e', fontSize: 12 }}>⏱ {elapsed}</Text>
        )}
      </Space>

      {/* Right: action buttons */}
      <Space size={6}>
        {!TERMINAL.has(task.status) && task.status !== 'RUNNING' && (
          <Tooltip title="Regenerate plan">
            <Button
              className="praxys-console-header-action praxys-console-header-icon-button"
              size="small"
              icon={<ReloadOutlined />}
              onClick={onRefreshPlan}
            />
          </Tooltip>
        )}
        {task.status === 'RUNNING' && (
          <Button
            className="praxys-console-header-action praxys-console-header-action-warning"
            size="small"
            icon={<PauseCircleOutlined />}
            onClick={onPause}
          >
            Pause
          </Button>
        )}
        {task.status === 'PAUSED' && (
          <Button className="praxys-console-header-action" size="small" icon={<PlayCircleOutlined />} onClick={onResume}>
            Resume
          </Button>
        )}
        {['RUNNING', 'PAUSED', 'WAITING_FOR_APPROVAL'].includes(task.status) && (
          <Button className="praxys-console-header-action praxys-console-header-action-danger" size="small" icon={<StopOutlined />} onClick={onCancel}>
            Cancel
          </Button>
        )}
        {task.status === 'COMPLETED' && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: '#2a2a2a',
              border: '1px solid #3f3f3f',
              borderRadius: 20,
              padding: '3px 12px',
              color: '#10b981',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 12 }} />
            Completed
          </span>
        )}
        {task.status === 'FAILED' && (
			<span
			  style={{
			    display: 'inline-flex',
			    alignItems: 'center',
			    gap: 5,
			    background: '#2a2a2a',
			    border: '1px solid #3f3f3f',
			    borderRadius: 20,
			    padding: '3px 12px',
			    color: '#10b981',
			    fontSize: 12,
			    fontWeight: 500,
			  }}
			>
			  <CheckCircleOutlined style={{ fontSize: 12 }} />
			  Failed
			</span>
        )}
      </Space>
    </div>
  );
};

export default TaskStatusHeader;
